// ════════════════════════════════════════════════════════════
// FichaCliente — vista 360º del cliente con cost control
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Alert, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, IconButton,
  Select, MenuItem, FormControl, InputLabel, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getFichaCliente, getCostControlCliente, crearMovimientoCCCliente, borrarMovimientoCC
} from '../api';

const eur = (n: number) =>
  (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function FichaCliente({ clienteId, puedeVerCC, onClose }: any) {
  const [ficha, setFicha] = useState<any>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    getFichaCliente(clienteId).then(setFicha).catch(() => setFicha(null));
  }, [clienteId]);

  if (!ficha) return (
    <Dialog open onClose={onClose}><DialogContent><CircularProgress /></DialogContent></Dialog>
  );

  const r = ficha.resumen || {};

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>🏢 {ficha.cliente.nombre}</DialogTitle>
      <DialogContent>
        {/* Resumen */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined"><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Máquinas</Typography>
              <Typography variant="h6" fontWeight={700}>{r.n_maquinas ?? 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined"><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Intervenciones</Typography>
              <Typography variant="h6" fontWeight={700}>{r.n_intervenciones ?? 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined"><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Abiertas</Typography>
              <Typography variant="h6" fontWeight={700}
                sx={{ color: r.intervenciones_abiertas > 0 ? '#f59e0b' : 'inherit' }}>
                {r.intervenciones_abiertas ?? 0}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined"><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Oportunidades vivas</Typography>
              <Typography variant="h6" fontWeight={700}>{eur(r.valor_oportunidades_vivas)}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
          <Tab label={`🏗 Máquinas (${ficha.maquinas.length})`} />
          <Tab label={`🔧 Intervenciones (${ficha.intervenciones.length})`} />
          <Tab label={`📊 Comercial (${ficha.oportunidades.length})`} />
          <Tab label={`📄 Ofertas (${ficha.ofertas.length})`} />
          {puedeVerCC && <Tab label="💰 Cost control" />}
        </Tabs>

        {/* MÁQUINAS */}
        {tab === 0 && (
          ficha.maquinas.length === 0
            ? <Alert severity="info">Este cliente no tiene máquinas asignadas.</Alert>
            : <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Modelo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nº Serie</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ubicación</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Garantía</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {ficha.maquinas.map((m: any) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.modelo}</TableCell>
                      <TableCell>{m.numero_serie}</TableCell>
                      <TableCell><Chip label={m.estado} size="small" /></TableCell>
                      <TableCell>{m.ubicacion || '—'}</TableCell>
                      <TableCell>{m.fin_garantia || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
        )}

        {/* INTERVENCIONES */}
        {tab === 1 && (
          ficha.intervenciones.length === 0
            ? <Alert severity="info">Sin intervenciones registradas.</Alert>
            : <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Máquina</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Horas</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {ficha.intervenciones.map((i: any) => (
                    <TableRow key={i.id}>
                      <TableCell>{i.fecha?.slice(0, 10)}</TableCell>
                      <TableCell>{i.tipo_intervencion}</TableCell>
                      <TableCell>{i.modelo} {i.numero_serie ? `#${i.numero_serie}` : ''}</TableCell>
                      <TableCell><Chip label={i.estado} size="small" /></TableCell>
                      <TableCell>{i.horas || 0}h</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
        )}

        {/* COMERCIAL */}
        {tab === 2 && (
          ficha.oportunidades.length === 0
            ? <Alert severity="info">Sin oportunidades registradas.</Alert>
            : <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Oportunidad</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Fase</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Comercial</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {ficha.oportunidades.map((o: any) => (
                    <TableRow key={o.id}>
                      <TableCell>{o.titulo}</TableCell>
                      <TableCell><Chip label={o.fase} size="small" /></TableCell>
                      <TableCell>{eur(o.valor_estimado)}</TableCell>
                      <TableCell>{o.comercial_nombre || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
        )}

        {/* OFERTAS */}
        {tab === 3 && (
          ficha.ofertas.length === 0
            ? <Alert severity="info">Sin ofertas registradas.</Alert>
            : <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {ficha.ofertas.map((o: any) => (
                    <TableRow key={o.id}>
                      <TableCell>{o.numero}</TableCell>
                      <TableCell>{o.fecha?.slice(0, 10)}</TableCell>
                      <TableCell><Chip label={o.estado} size="small" /></TableCell>
                      <TableCell>{eur(o.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
        )}

        {/* COST CONTROL */}
        {tab === 4 && puedeVerCC && <CostControlCliente clienteId={clienteId} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Cost control del cliente ──────────────────────────────
function CostControlCliente({ clienteId }: { clienteId: number }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    tipo: 'gasto', fecha: new Date().toISOString().slice(0, 10),
    concepto: '', importe: '', linea_negocio: 'otros'
  });
  const [guardando, setGuardando] = useState(false);

  const cargar = () => {
    getCostControlCliente(clienteId).then(setData)
      .catch((e: any) => setError(e.response?.data?.detail || 'Error al cargar'));
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [clienteId]);

  const imputar = async () => {
    setGuardando(true);
    try {
      await crearMovimientoCCCliente(clienteId, { ...form, importe: parseFloat(form.importe) });
      setForm({ ...form, concepto: '', importe: '' });
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al imputar');
    } finally { setGuardando(false); }
  };

  const borrar = async (id: number) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try { await borrarMovimientoCC(id); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress />;

  const t = data.totales;

  return (
    <Box>
      {/* Totales globales */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: '#fef2f2' }}><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Gastos</Typography>
            <Typography fontWeight={800} sx={{ color: '#dc2626' }}>{eur(t.gastos)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: '#f0fdf4' }}><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Ingresos</Typography>
            <Typography fontWeight={800} sx={{ color: '#16a34a' }}>{eur(t.ingresos)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: t.resultado >= 0 ? '#f0fdf4' : '#fef2f2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Resultado</Typography>
              <Typography fontWeight={800} sx={{ color: t.resultado >= 0 ? '#16a34a' : '#dc2626' }}>
                {eur(t.resultado)}
              </Typography>
            </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Margen</Typography>
            <Typography fontWeight={800}>{t.margen_pct === null ? '—' : `${t.margen_pct}%`}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Desglose por máquina */}
      {data.por_maquina.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Desglose por máquina</Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Máquina</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Gastos</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Ingresos</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Resultado</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.por_maquina.map((m: any) => (
                <TableRow key={m.id}>
                  <TableCell>{m.modelo} #{m.numero_serie}</TableCell>
                  <TableCell align="right">{eur(m.totales.gastos)}</TableCell>
                  <TableCell align="right">{eur(m.totales.ingresos)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700,
                    color: m.totales.resultado >= 0 ? '#16a34a' : '#dc2626' }}>
                    {eur(m.totales.resultado)}
                  </TableCell>
                </TableRow>
              ))}
              {data.directos.n_apuntes > 0 && (
                <TableRow>
                  <TableCell><i>Directo al cliente (sin máquina)</i></TableCell>
                  <TableCell align="right">{eur(data.directos.totales.gastos)}</TableCell>
                  <TableCell align="right">{eur(data.directos.totales.ingresos)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {eur(data.directos.totales.resultado)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Imputar movimiento al cliente */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Imputar movimiento al cliente</Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={form.tipo} label="Tipo"
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <MenuItem value="gasto">🔴 Gasto</MenuItem>
                  <MenuItem value="ingreso">🟢 Ingreso</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="date" label="Fecha"
                InputLabelProps={{ shrink: true }} value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Concepto (libre)" value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" label="Importe" value={form.importe}
                onChange={(e) => setForm({ ...form, importe: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button fullWidth variant="contained" startIcon={<AddIcon />}
                disabled={guardando || !form.concepto.trim() || !form.importe}
                onClick={imputar} sx={{ bgcolor: '#4f46e5' }}>
                Imputar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Movimientos */}
      {data.apuntes.length === 0 ? (
        <Alert severity="info">Sin movimientos todavía.</Alert>
      ) : (
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Máquina</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Origen</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
            <TableCell />
          </TableRow></TableHead>
          <TableBody>
            {data.apuntes.map((a: any) => (
              <TableRow key={a.id}>
                <TableCell>{a.fecha}</TableCell>
                <TableCell>{a.concepto}</TableCell>
                <TableCell>{a.modelo ? `${a.modelo} #${a.numero_serie}` : '—'}</TableCell>
                <TableCell>
                  <Chip label={a.origen_tipo === 'manual' ? 'Manual' : a.origen_tipo} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700,
                  color: a.tipo === 'gasto' ? '#dc2626' : '#16a34a' }}>
                  {a.tipo === 'gasto' ? '−' : '+'}{eur(a.importe)}
                </TableCell>
                <TableCell align="right">
                  {a.origen_tipo === 'manual' && (
                    <IconButton size="small" color="error" onClick={() => borrar(a.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}